import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { money } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function PaymentReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | success | pending | error
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setInfo(data);
          setState("success");
          return;
        }
        if (data.status === "expired") {
          setState("error");
          return;
        }
      } catch (e) {
        // Transient error while polling payment status — retry silently below.
      }
      attempts += 1;
      if (attempts >= 6) {
        setState("pending");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId]);

  const labels = { application_fee: "Application Fee", security_deposit: "Security Deposit", monthly_rent: "Monthly Rent", upgrade: "Plan Upgrade" };

  return (
    <div className="min-h-screen bg-bone">
      <Navbar />
      <div className="max-w-md mx-auto px-5 py-24 text-center">
        <div className="bg-white border border-line rounded-2xl shadow-sm p-10">
          {state === "checking" && (
            <>
              <Loader2 className="h-12 w-12 animate-spin text-forest mx-auto" />
              <h1 className="mt-5 text-xl font-heading font-semibold text-ink">Confirming your payment…</h1>
              <p className="text-sm text-mute mt-1">This only takes a moment.</p>
            </>
          )}
          {state === "success" && (
            <>
              <CheckCircle2 className="h-14 w-14 text-emerald-600 mx-auto" />
              <h1 data-testid="payment-success" className="mt-5 text-2xl font-heading font-semibold text-ink">Payment successful</h1>
              {info && <p className="text-mute mt-2">{labels[info.purpose] || "Payment"} — <span className="font-medium text-forest">{money(info.amount)}</span></p>}
              <button data-testid="payment-return-dashboard" onClick={() => navigate("/dashboard")} className="mt-6 bg-forest text-white px-6 py-2.5 rounded-lg font-medium hover:bg-forest-hover transition-colors">
                Go to Dashboard
              </button>
            </>
          )}
          {state === "pending" && (
            <>
              <Loader2 className="h-12 w-12 text-amber-500 mx-auto" />
              <h1 className="mt-5 text-xl font-heading font-semibold text-ink">Still processing</h1>
              <p className="text-sm text-mute mt-1">Your payment is being processed. Check your dashboard shortly.</p>
              <button onClick={() => navigate("/dashboard")} className="mt-6 bg-forest text-white px-6 py-2.5 rounded-lg font-medium hover:bg-forest-hover transition-colors">Go to Dashboard</button>
            </>
          )}
          {state === "error" && (
            <>
              <XCircle className="h-14 w-14 text-red-500 mx-auto" />
              <h1 className="mt-5 text-xl font-heading font-semibold text-ink">Payment not completed</h1>
              <p className="text-sm text-mute mt-1">Something went wrong or the session expired.</p>
              <button onClick={() => navigate("/dashboard")} className="mt-6 bg-forest text-white px-6 py-2.5 rounded-lg font-medium hover:bg-forest-hover transition-colors">Back to Dashboard</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
